import React, { useState, useEffect } from 'react';
import { adminReferralAPI } from '../../lib/api';
import { Send, Check, X, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const AdminReferralPayoutsPage = () => {
  const [payouts, setPayouts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [processing, setProcessing] = useState('');

  useEffect(() => { fetchPayouts(); }, []);

  const fetchPayouts = async (status) => {
    setLoading(true);
    try {
      const res = await adminReferralAPI.getPayouts(1, 50, status || null);
      setPayouts(res.data.payouts || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load payouts'); }
    finally { setLoading(false); }
  };

  const handleProcess = async (id) => {
    setProcessing(id);
    try {
      await adminReferralAPI.processPayout(id);
      toast.success('Payout marked as paid!');
      fetchPayouts(filter);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to process'); }
    finally { setProcessing(''); }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this payout? Amount will be refunded to user wallet.')) return;
    setProcessing(id);
    try {
      await adminReferralAPI.rejectPayout(id);
      toast.success('Payout rejected and refunded');
      fetchPayouts(filter);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reject'); }
    finally { setProcessing(''); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div>
        <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Payout Requests</h1>
        <p className="text-[#5F6368]">Review and process user payout requests</p>
      </div>

      <div className="dashboard-card">
        {/* Filters */}
        <div className="flex items-center gap-2 mb-4">
          {['', 'pending', 'paid', 'rejected'].map(s => (
            <button key={s} onClick={() => { setFilter(s); fetchPayouts(s); }}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${filter === s ? 'bg-[#1A73E8] text-white' : 'bg-[#F1F3F4] text-[#5F6368] hover:bg-[#E8EAED]'}`}
              data-testid={`payout-filter-${s || 'all'}`}
            >{s || 'All'}</button>
          ))}
          <span className="text-xs text-[#5F6368] ml-auto">{total} total</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
          </div>
        ) : payouts.length === 0 ? (
          <div className="text-center py-8">
            <Send className="w-10 h-10 text-[#DADCE0] mx-auto mb-2" />
            <p className="text-sm text-[#5F6368]">No payout requests found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-payouts-table">
              <thead>
                <tr className="border-b border-[#DADCE0]">
                  <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">User</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Amount</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Method</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Details</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Status</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Date</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-[#5F6368]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map(p => (
                  <tr key={p.id} className="border-b border-[#F1F3F4]">
                    <td className="py-2.5 px-3">
                      <p className="font-medium">{p.user_name}</p>
                      <p className="text-xs text-[#5F6368]">{p.user_email}</p>
                    </td>
                    <td className="py-2.5 px-3 font-semibold">{p.currency === 'INR' ? '₹' : '$'}{p.amount?.toFixed(2)}</td>
                    <td className="py-2.5 px-3 uppercase text-xs font-medium">{p.method}</td>
                    <td className="py-2.5 px-3 text-xs text-[#5F6368]">
                      {p.method === 'upi' && p.upi_id && <span>UPI: {p.upi_id}</span>}
                      {p.method === 'paypal' && p.paypal_email && <span>PayPal: {p.paypal_email}</span>}
                      {p.method === 'wallet' && <span>Wallet</span>}
                    </td>
                    <td className="py-2.5 px-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        p.status === 'paid' ? 'bg-[#E6F4EA] text-[#1E8E3E]' :
                        p.status === 'rejected' ? 'bg-[#FCE8E6] text-[#D93025]' :
                        'bg-[#FEF7E0] text-[#F9AB00]'
                      }`}>{p.status}</span>
                    </td>
                    <td className="py-2.5 px-3 text-[#5F6368] text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="py-2.5 px-3">
                      {p.status === 'pending' && (
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleProcess(p.id)} disabled={processing === p.id}
                            className="text-xs px-2.5 py-1.5 bg-[#1E8E3E] text-white rounded-lg hover:bg-[#137333] disabled:opacity-50 flex items-center gap-1"
                            data-testid={`process-${p.id}`}
                          ><Check className="w-3 h-3" /> Pay</button>
                          <button onClick={() => handleReject(p.id)} disabled={processing === p.id}
                            className="text-xs px-2.5 py-1.5 bg-[#D93025] text-white rounded-lg hover:bg-[#B31412] disabled:opacity-50 flex items-center gap-1"
                            data-testid={`reject-${p.id}`}
                          ><X className="w-3 h-3" /> Reject</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReferralPayoutsPage;
